import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { ToastPositionEnum } from 'src/app/shared/services/toast/toast-position.enum';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import {
  PermissionEntry,
  PermissionGroupCatalogEntry,
} from 'src/app/shared/components/permission-selector/permission-selector.component';
import { FrontFirstTreeGroupDto } from 'src/app/shared/components/front-first-permission-tree/front-first-permission-tree.types';
import {
  PlanPermissionCatalog,
  PlanPermissionService,
} from './crud-service';

/**
 * Permissões do Plano — piloto do SELETOR ÚNICO de permissões (componente
 * consolidado do ABCX, ver specs/permissions/permission-selector-consolidation.md).
 *
 * O componente `app-permission-selector` só expõe um `selectedIds` flat (não
 * tem saída separada de "grupos cheios") — o vínculo dinâmico (groupIds) é
 * DERIVADO aqui no caller a partir do catálogo + `selectedIds` a cada mudança
 * (ver {@link computeFullGroupIds}), igual à leitura inicial do backend.
 * Backend/contrato inalterados: GET catalog → {groups,selectedParamIds,
 * selectedGroupIds}; PUT selection → {plan,groupIds,paramIds}.
 */
@Component({
  selector: 'app-plan-permissions',
  templateUrl: './plan-permissions.component.html',
  styleUrls: ['./plan-permissions.component.sass'],
  providers: [PlanPermissionService],
})
export class PlanPermissionsComponent implements OnInit {
  element: any;
  title = 'Permissões do Plano';

  catalog: PermissionGroupCatalogEntry[] = [];
  permissions: PermissionEntry[] = [];
  selectedIds: string[] = [];

  loading = false;
  applied = false;
  /** Árvore "Por tela" com o universo COMPLETO do produto. Vazia/erro → só Simples/Avançado. */
  frontFirstGroups: FrontFirstTreeGroupDto[] = [];
  frontFirstLoading = false;

  constructor(
    public dialog: MatDialog,
    private service: PlanPermissionService,
    public dialogRef: MatDialogRef<PlanPermissionsComponent>,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA)
    data: {
      type: TypeIndexEnum;
      select: boolean;
      element: any;
    }
  ) {
    this.element = data.element;
    this.title = `${this.title} ${this.element?.name ?? ''}`.trim();
  }

  ngOnInit(): void {
    this.dialogRef
      .beforeClosed()
      .subscribe(() =>
        this.dialogRef.close({ element: { applied: this.applied } })
      );
    this.loadCatalog();
    this.loadFrontFirstTree();
  }

  /** Carrega a árvore "Por tela"; qualquer falha só esconde o modo (Simples/Avançado seguem). */
  public loadFrontFirstTree(): void {
    this.frontFirstLoading = true;
    this.service.getFrontFirstTree().subscribe({
      next: (res) => {
        this.frontFirstGroups = Array.isArray(res?.groups) ? res.groups : [];
        this.frontFirstLoading = false;
      },
      error: () => {
        this.frontFirstGroups = [];
        this.frontFirstLoading = false;
      },
    });
  }

  private get planId(): string {
    return this.element?.id;
  }

  public loadCatalog(): void {
    if (!this.planId) return;
    this.loading = true;
    this.service.getCatalog(this.planId).subscribe({
      next: (res: PlanPermissionCatalog) => this.applyCatalog(res),
      error: () => {
        this.loading = false;
        this.toastService.showError(MessagesEnum.ERROR_UPDATING);
      },
    });
  }

  /** Mapeia {groups,selectedParamIds,selectedGroupIds} → contrato do app-permission-selector. */
  private applyCatalog(res: PlanPermissionCatalog): void {
    const groups = res?.groups || [];

    this.catalog = groups.map((g) => ({
      id: String(g.id),
      name: g.name,
      permissionIds: (g.subgroups || []).flatMap((s) =>
        (s.permissions || []).map((p) => String(p.id))
      ),
      subgroups: (g.subgroups || []).map((s) => ({
        key: `${g.id}::${s.key}`,
        name: s.label,
        permissionIds: (s.permissions || []).map((p) => String(p.id)),
      })),
    }));

    this.permissions = groups.flatMap((g) =>
      (g.subgroups || []).flatMap((s) =>
        (s.permissions || []).map((p) => ({
          id: String(p.id),
          name: p.label || p.name,
          groupHint: g.name,
        }))
      )
    );

    // Reconstrói a seleção do componente: granular ∪ (folhas dos grupos vinculados).
    const boundGroupIds = new Set((res?.selectedGroupIds || []).map((x) => String(x)));
    const selected = new Set((res?.selectedParamIds || []).map((x) => String(x)));
    for (const g of this.catalog) {
      if (boundGroupIds.has(g.id)) {
        for (const id of g.permissionIds) selected.add(id);
      }
    }
    this.selectedIds = [...selected];
    this.loading = false;
  }

  /** Grupos com TODAS as folhas em `ids` — vínculo dinâmico (derivado, não vem do componente). */
  private computeFullGroupIds(ids: string[]): string[] {
    const set = new Set(ids);
    return this.catalog
      .filter((g) => g.permissionIds.length > 0 && g.permissionIds.every((id) => set.has(id)))
      .map((g) => g.id);
  }

  onSelectedIdsChange(ids: string[]): void {
    this.selectedIds = ids;
  }

  public hasValue(): boolean {
    return (this.selectedIds?.length || 0) > 0;
  }

  public confirm(): void {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: 'CONFIRMAÇÃO' },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (!data?.result) return;

        const groupIds = this.computeFullGroupIds(this.selectedIds);
        const coveredByGroup = new Set(
          this.catalog
            .filter((g) => groupIds.includes(g.id))
            .flatMap((g) => g.permissionIds)
        );
        const paramIds = (this.selectedIds || []).filter((id) => !coveredByGroup.has(id));

        this.service
          .saveSelection({ plan: this.planId, groupIds, paramIds })
          .subscribe({
            next: (res: PlanPermissionCatalog) => {
              this.applyCatalog(res);
              this.applied = true;
              this.toastService.showSuccess(MessagesEnum.SUCCESS_OPERATION, {
                position: ToastPositionEnum.BOTTOM_END,
                toast: true,
                timer: 3000,
                showConfirmButton: false,
                backdrop: '',
              });
            },
            error: (err: any) => {
              const msg =
                err?.status === 403
                  ? 'Você não pode conceder permissões que não possui.'
                  : MessagesEnum.ERROR_UPDATING;
              this.toastService.showError(msg);
            },
          });
      },
    });
  }
}
